<template>
  <div>
    <div class="py-5 container-fluid">
      <div id="topinfo">
        <div class="row mt-4 justify-content-center">
          <div class="col-12">
            <div class="mx-auto col-xl-9 col-lg-10 col-md-11 col-sm-12 text-start mt-3 mb-0">
              <button
                class="btn btn-outline-secondary btn-sm px-3 ms-2 mt-1"
                @click="$router.go(-1)"
              >
                <font-awesome-icon :icon="['fas', 'arrow-rotate-left']" />
                Go back
              </button>
            </div>
            <div
              class="mx-auto col-xl-9 col-lg-10 col-md-11 col-sm-12 d-flex mb-2 flex-column card card-body blur shadow-blur"
            >
              <div class="card-header text-center">
                <h3 class="font-weight-bolder text-success text-gradient">Thickness Adjustment</h3>
                <h5
                  v-if="coating_booth.name"
                  class="mb-0"
                >
                  for Line <i>{{ coating_booth.name }}</i>
                </h5>
                <p class="mt-4 mb-0 text-justify">
                  Thickness Adjustment suggests a reduction on the powder amount settings to achieve powder savings.
                </p>
              </div>
            </div>
            <div
              class="mx-auto col-xl-9 col-lg-10 col-md-11 col-sm-12 d-flex mb-2 flex-column card card-body blur shadow-blur"
            >
              <div class="card-header row mb-0 pb-0">
                <h4
                  v-if="selected_hopper === null"
                  class="text-center mt-1 ms-4"
                >
                  Select Hopper
                </h4>
                <label
                  v-if="selected_hopper !== null"
                  class="mt-2 mb-2"
                >
                  Selected Hopper
                </label>
                <div
                  class="mt-2 mb-2"
                  :style="{ position: 'relative', pointerEvents: hoppers.length === 1 ? 'none' : 'auto' }"
                  @click.stop="
                    hoppers.length > 1 && (hopperDropdownVisible = !hopperDropdownVisible);
                    recipeDropdownVisible = false;
                  "
                >
                  <button class="form-control">
                    {{
                      selected_hopper
                        ? selected_hopper.name
                        : hoppers.length > 0
                          ? "Click here to select a Hopper"
                          : "No Hoppers to select"
                    }}
                  </button>
                  <div
                    v-if="hopperDropdownVisible"
                    class="dropdown-menu show w-100"
                    style="border: 1px solid #ccc; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1)"
                  >
                    <input
                      v-model="searchQueryHopper"
                      class="form-control mt-1 mb-2 ms-2 w-90"
                      type="text"
                      placeholder="Search..."
                      @click.stop
                    />
                    <div
                      v-for="hopper in filteredHoppers"
                      :key="hopper.id"
                      class="dropdown-item"
                      style="cursor: pointer"
                      @click="updateSelectedHopper(hopper)"
                    >
                      {{ hopper.name }}
                    </div>
                  </div>
                </div>
                <div v-if="selected_hopper">
                  <h4
                    v-if="selected_recipe === null"
                    class="text-center mt-1 ms-4"
                  >
                    Select Recipe
                  </h4>
                  <label
                    v-if="selected_recipe !== null"
                    class="mt-2 mb-2"
                  >
                    Selected Recipe
                  </label>
                  <div
                    class="mt-2 mb-2"
                    :style="{ position: 'relative', pointerEvents: recipes.length === 1 ? 'none' : 'auto' }"
                    @click.stop="
                      recipes.length > 1 && (recipeDropdownVisible = !recipeDropdownVisible);
                      hopperDropdownVisible = false;
                    "
                  >
                    <button class="form-control">
                      {{
                        selected_recipe
                          ? selected_recipe.category
                          : recipes.length > 0
                            ? "Click here to select a Recipe"
                            : "No Recipe to select"
                      }}
                    </button>
                    <div
                      v-if="recipeDropdownVisible"
                      class="dropdown-menu show w-100"
                      style="border: 1px solid #ccc; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1)"
                    >
                      <input
                        v-model="searchQueryRecipe"
                        class="form-control mt-1 ms-2 w-90"
                        type="text"
                        placeholder="Search..."
                        @click.stop
                      />
                      <div
                        v-for="recipe in filteredRecipes"
                        :key="recipe.id"
                        class="dropdown-item"
                        style="cursor: pointer"
                        @click="
                          updateSelectedRecipe(recipe);
                          clearThicknessAdjustmentsHistory(false);
                        "
                      >
                        {{ recipe.category }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="selected_recipe != null"
                class="card-body"
              >
                <LineLayoutVisualization
                  v-if="showLineLayoutVisualization"
                  :coating-booth="coating_booth"
                />
                <h5 class="mb-3">Thickness Requirements</h5>
                <div class="d-flex justify-content-left mb-4">
                  <div class="px-2">
                    <label>
                      Minimum Desired Thickness
                      <i
                        title="Minimum Thickness required for the coating to be considered acceptable"
                        class="fa fa-info-circle"
                        aria-hidden="true"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-container="body"
                        data-animation="true"
                      >
                      </i>
                    </label>
                    <input
                      v-model.lazy="minimum_target_thickness"
                      type="number"
                      min="0"
                      max="10000"
                      class="form-control form-control-lg"
                      placeholder=""
                      style="font-size: 1.25rem; min-width: 100px; max-width: 140px"
                      @keydown="numericPositiveOnly"
                      @change="
                        patchLatestThicknessAdjustment(
                          minimum_target_thickness,
                          target_throughput,
                          powder_amount_settings,
                          measured_thickness_mean,
                          measured_thickness_std,
                          expected_thickness_mean,
                          expected_thickness_std,
                        )
                      "
                    />
                  </div>
                </div>
                <div
                  v-if="filledMinimumTargetThickness"
                  class="row"
                >
                  <h5 class="mb-3">Recipe Parameters</h5>
                  <div class="d-flex justify-content-left">
                    <div class="px-2">
                      <MiniStatisticsCard
                        title="Target throughput"
                        :value="`${target_throughput.toFixed(1)}`"
                        percentage=""
                        :icon="{ component: 'ni ni ni-support-16', background: 'bg-gradient-info' }"
                        class-content="px-2"
                      />
                    </div>
                    <div
                      v-if="measured_thickness_mean != -1 && measured_thickness_mean != null && firstAdjustmentDone"
                      class="px-4"
                    >
                      <MiniStatisticsCard
                        :title="
                          isInLastPage && firstAdjustmentDone ? 'Expected Mean Thickness' : 'Measured Mean Thickness'
                        "
                        :value="measured_thickness_mean"
                        percentage=""
                        :icon="{ component: 'fa fa-bars fa-3x', background: 'bg-gradient-info' }"
                        class-content="px-2"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="filledMinimumTargetThickness && target_throughput != null">
                  <h6 class="mb-3">Side reciprocator guns and their settings:</h6>

                  <table class="table text-center table-sm">
                    <thead>
                      <tr>
                        <th class="small">Gun Name</th>
                        <th class="small">Powder Amount Setting</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(gun, gunIndex) in gun_data.filter(gun => gun.is_gun_on_bottom_reciprocator === false)"
                        :key="gunIndex"
                      >
                        <td class="small">{{ gun.name }}</td>
                        <td class="small">
                          <b
                            >{{
                              powder_amount_settings != null &&
                              powder_amount_settings[gunIndex] != null &&
                              powder_amount_settings[gunIndex] != undefined
                                ? parseFloat(parseFloat(powder_amount_settings[gunIndex]).toFixed(1))
                                : "-"
                            }}
                          </b>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div
                    v-if="
                      coating_booth.reciprocator_side_bottom !== null &&
                      coating_booth.reciprocator_side_bottom !== undefined
                    "
                    class="bottom-gun-table"
                  >
                    <h6 class="mb-3">
                      Bottom reciprocator guns and their settings:
                      <i
                        :title="`Bottom reciprocator guns have a separate target throughput of ${selected_recipe.target_throughput_bottom} and are not considered in the thickness adjustment calculations.`"
                        class="fa fa-info-circle"
                        aria-hidden="true"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-container="body"
                        data-animation="true"
                      >
                      </i>
                    </h6>
                    <table class="table text-center table-sm">
                      <thead>
                        <tr>
                          <th class="small">Gun Name</th>
                          <th class="small">Powder Amount Setting</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="gun in gun_data.filter(gun => gun.is_gun_on_bottom_reciprocator === true)"
                          :key="gun.gun_index"
                        >
                          <td class="small">{{ gun.name }}</td>
                          <td class="small">
                            <b
                              >{{
                                powder_amount_settings != null &&
                                powder_amount_settings[gun.gun_index - 1] != null &&
                                powder_amount_settings[gun.gun_index - 1] != undefined
                                  ? parseFloat(parseFloat(powder_amount_settings[gun.gun_index - 1]).toFixed(1))
                                  : "-"
                              }}
                            </b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <h5 class="mb-3">Coating Experiement Results</h5>
                  <div class="d-flex justify-content-left mb-2">
                    <div class="px-2">
                      <div v-if="!isInLastPage">
                        <MiniStatisticsCard
                          title="Mean Thickness Measured"
                          :value="`${measured_thickness_mean}`"
                          percentage=""
                          :icon="{ component: 'fa fa-bars fa-3x', background: 'bg-gradient-info' }"
                          class-content="px-2"
                        />
                      </div>
                      <div v-else>
                        <label>
                          Mean Thickness Measured
                          <i
                            title="Measured Mean Thickness of the coating"
                            class="fa fa-info-circle"
                            aria-hidden="true"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            data-container="body"
                            data-animation="true"
                          >
                          </i>
                        </label>
                        <input
                          v-model.lazy="measured_thickness_mean"
                          type="number"
                          min="0"
                          max="10000"
                          class="form-control form-control-lg"
                          placeholder=""
                          style="font-size: 1.25rem; min-width: 100px; max-width: 140px"
                          @blur="
                            patchLatestThicknessAdjustment(
                              minimum_target_thickness,
                              target_throughput,
                              powder_amount_settings,
                              measured_thickness_mean,
                              measured_thickness_std,
                              expected_thickness_mean,
                              expected_thickness_std,
                            )
                          "
                        />
                      </div>
                    </div>
                    <div class="px-4">
                      <div v-if="!isInLastPage">
                        <MiniStatisticsCard
                          title="Standard Deviation Measured (std)"
                          :value="`${measured_thickness_std}`"
                          percentage=""
                          :icon="{ component: 'fa fa-bars fa-3x', background: 'bg-gradient-info' }"
                          class-content="px-2"
                        />
                      </div>
                      <div v-else>
                        <label>
                          Standard Deviation Measured (std)
                          <i
                            title="Standard Deviation of Thickness for the coating"
                            class="fa fa-info-circle"
                            aria-hidden="true"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            data-container="body"
                            data-animation="true"
                          >
                          </i>
                        </label>
                        <input
                          v-model.lazy="measured_thickness_std"
                          type="number"
                          min="0"
                          max="10000"
                          class="form-control form-control-lg"
                          placeholder=""
                          style="font-size: 1.25rem; min-width: 100px; max-width: 140px"
                          @blur="
                            patchLatestThicknessAdjustment(
                              minimum_target_thickness,
                              target_throughput,
                              powder_amount_settings,
                              measured_thickness_mean,
                              measured_thickness_std,
                              expected_thickness_mean,
                              expected_thickness_std,
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row px-3 mt-3 mb-0">
                    <button
                      v-if="measured_thickness_mean !== null && measured_thickness_std !== null && isInLastPage"
                      class="btn btn-primary w-50 mt-0 mb-0"
                      style="min-width: 300px; max-width: 380px"
                      @click="drawThicknessGraph()"
                    >
                      Show Thickness Distribution Chart
                    </button>
                  </div>
                  <hr class="horizontal dark" />
                  <div class="row mt-2">
                    <div class="col-7">
                      <GaussianChart
                        :show-thickness-chart-title="show_thickness_chart_title"
                        title="Thickness Distribution Chart"
                        chart-name="gaussianThicknessAdjustmentChart"
                        class="mt-2"
                      />
                    </div>
                    <div
                      v-if="firstAdjustmentDone && currentPage > 0"
                      class="col-5"
                    >
                      <div class="mt-6 justify-content-center">
                        <SteelcasePowderSavingChart
                          :dataset-zero="getPreviousTargetThroughput()"
                          :dataset-one="target_throughput ? target_throughput : 0"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="firstAdjustmentDone"
                    class="text-center mt-4"
                  >
                    <h6>Explore history of Throughput adjustments</h6>
                    <soft-pagination
                      class="pagination-lg justify-content-center"
                      color="primary"
                      aria-label="Experiment thickness measures input pages"
                    >
                      <soft-pagination-item
                        prev
                        :disabled="isInFirstPage"
                        @click="onClickPrevPage()"
                      />
                      <soft-pagination-item
                        v-for="(item, index) in thickness_adjustment_history"
                        :key="(index + 1).toFixed(0)"
                        :label="index.toFixed(0)"
                        :active="currentPage === index"
                        @click="updateCurrentPage(index)"
                      />
                      <soft-pagination-item
                        next
                        :disabled="isInLastPage"
                        @click="onClickNextPage()"
                      />
                    </soft-pagination>
                  </div>
                  <div class="row mt-2">
                    <div
                      v-if="firstAdjustmentDone"
                      class="col-4"
                    >
                      <button
                        class="btn btn-warning"
                        style="width: 100%"
                        @click="clearThicknessAdjustmentsHistory()"
                      >
                        Clear history & restart process
                      </button>
                    </div>
                    <div :class="firstAdjustmentDone ? 'col-8' : 'col-12'">
                      <button
                        :class="`btn ${coatingMeasurementReady ? 'btn-success' : 'btn-secondary'}`"
                        style="width: 100%"
                        :disabled="!isInLastPage || !coatingMeasurementReady"
                        @click="computeNextThicknessAdjustment()"
                      >
                        {{
                          !isInLastPage
                            ? "SHOWING HISTORY"
                            : coatingMeasurementReady
                              ? "Compute Adjusted Recipe Settings"
                              : "Input new Measurements to adjust recipe"
                        }}
                      </button>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div
                      v-if="firstAdjustmentDone && isInLastPage"
                      class="col-12"
                    >
                      <button
                        class="btn btn-primary"
                        style="width: 100%"
                        @click="updateRecipeSettings()"
                      >
                        UPDATE RECIPE WITH NEW SETTINGS
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { faArrowRotateLeft, faCircleChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";

import axios from "axios";
import eventBus from "@/views/applications/blueprint/utils/eventBus";
import setTooltip from "@/assets/js/tooltip.js";
import {
  gaussian,
  generateXforMultipleMuSigma,
  generateYwithXvalues,
} from "@/views/applications/blueprint/utils/gaussianchart";

import { numericPositiveOnly } from "@/views/composables.js";

import GaussianChart from "@/views/applications/blueprint/components/GaussianChart.vue";
import MiniStatisticsCard from "@/components/MiniStatisticsCard.vue";
import SteelcasePowderSavingChart from "./components/SteelcasePowderSavingChart.vue";
import SoftPagination from "@/components/SoftPagination.vue";
import SoftPaginationItem from "@/components/SoftPaginationItem.vue";
import LineLayoutVisualization from "./components/LineLayoutVisualization.vue";

library.add([faArrowRotateLeft, faCircleChevronRight]);

export default {
  name: "ThicknessAdjustment",
  components: {
    FontAwesomeIcon,
    GaussianChart,
    MiniStatisticsCard,
    SteelcasePowderSavingChart,
    SoftPagination,
    SoftPaginationItem,
    LineLayoutVisualization,
  },
  props: {
    lineId: {
      type: String,
      default: "",
    },
  },
  data() {
    this.$i18n.locale = this.$store.state.user_data.language;
    return {
      selected_powder: null,
      measured_thickness_mean: null,
      measured_thickness_std: null,
      total_line_guns: 0,
      minimum_target_thickness: null,
      minimum_throughput: null,
      percentile: 0.1,
      std_previous_thickness_measurements: null,
      // Powder Amount Calibration variables
      target_throughput: null,
      invalid_powder_amount_shown: false,
      thickness_adjustment_history: [],
      latest_thickness_adjustment: {
        id: null,
        measured_thickness_mean: null,
        measured_thickness_std: null,
      },
      powder_amount_settings: null,
      show_thickness_chart_title: "false",
      powder_amount_out_of_range_alert_shown: false,
      // Pagination variables
      currentPage: 0,
      // Line Layout image variables
      showLineLayoutVisualization: false,
      // From View Recipes
      coating_booth: {},
      selected_hopper: null,
      selected_recipe: null,
      hopperDropdownVisible: false,
      recipeDropdownVisible: false,
      searchQueryHopper: "",
      searchQueryRecipe: "",
      hoppers: [],
      recipes: [],
      gun_data: [],
    };
  },
  computed: {
    filteredHoppers() {
      return this.hoppers.filter(hopper => hopper.name.toLowerCase().includes(this.searchQueryHopper.toLowerCase()));
    },
    filteredRecipes() {
      return this.recipes.filter(recipe =>
        recipe.category.toLowerCase().includes(this.searchQueryRecipe.toLowerCase()),
      );
    },
    filledMinimumTargetThickness() {
      return (
        this.minimum_target_thickness !== null &&
        this.minimum_target_thickness !== "" &&
        this.minimum_target_thickness !== undefined
      );
    },
    firstAdjustmentDone() {
      return this.thickness_adjustment_history.length > 1;
    },
    isInFirstPage() {
      return this.currentPage === 0;
    },
    isInLastPage() {
      return this.currentPage === this.thickness_adjustment_history.length - 1;
    },
    emptyCoatingMeasures() {
      return (
        this.measured_thickness_mean == null ||
        this.measured_thickness_mean == undefined ||
        this.measured_thickness_mean == "" ||
        this.measured_thickness_std == null ||
        this.measured_thickness_std == undefined ||
        this.measured_thickness_std == ""
      );
    },
    coatingMeasurementReady() {
      return !this.emptyCoatingMeasures && this.isInLastPage;
    },
    currentPageCoatingMeasuresFilled() {
      return (
        this.thickness_adjustment_history[this.currentPage].measured_thickness_mean != null &&
        this.thickness_adjustment_history[this.currentPage].measured_thickness_mean != undefined &&
        this.thickness_adjustment_history[this.currentPage].measured_thickness_mean != "" &&
        this.thickness_adjustment_history[this.currentPage].measured_thickness_std != null &&
        this.thickness_adjustment_history[this.currentPage].measured_thickness_std != undefined &&
        this.thickness_adjustment_history[this.currentPage].measured_thickness_std != ""
      );
    },
  },
  mounted() {
    this.$store.state.isAbsolute = true;
    this.getData();
    setTooltip();
    document.addEventListener("click", this.closeDropdown);
  },
  beforeUnmount() {
    this.$store.state.isAbsolute = false;
  },
  methods: {
    gaussian,
    generateXforMultipleMuSigma,
    generateYwithXvalues,
    numericPositiveOnly,
    async getData() {
      await this.getCoatingBooth();

      await this.getHoppers();

      if (this.$route.query.hopper) {
        const query_hopper = this.hoppers.find(hopper => hopper.id == this.$route.query.hopper);
        await this.updateSelectedHopper(query_hopper);
      }

      if (this.selected_hopper !== null && this.$route.query.recipe) {
        const query_recipe = this.recipes.find(recipe => recipe.id == this.$route.query.recipe);
        await this.updateSelectedRecipe(query_recipe);
      }

      await this.getMinMaxThroughput();

      if (this.selected_hopper !== null && this.selected_recipe !== null) {
        await this.getThicknessAdjustmentHistory();
      }

      if (this.coatingMeasurementReady) {
        this.drawThicknessGraph();
      }
    },
    async getCoatingBooth() {
      try {
        var response = await axios.get("api/v1/steelcase/coatingbooth/" + this.lineId + "/");
        this.coating_booth = response.data;
        this.gun_data = [
          ...this.coating_booth.reciprocator_side_A.guns,
          ...this.coating_booth.reciprocator_side_B.guns,
          ...(this.coating_booth.reciprocator_side_bottom ? this.coating_booth.reciprocator_side_bottom.guns : []),
        ];
        this.total_line_guns = this.gun_data.length;
      } catch (error) {
        console.error(error);
      }
    },
    async updateSelectedHopper(hopper) {
      console.log("updateSelectedHopper()", hopper);
      this.searchQueryHopper = "";
      if (this.selected_hopper != hopper) {
        this.selected_recipe = null;
        this.target_throughput = null;
        this.selected_hopper = hopper;
        await this.getRecipes();

        const selected_recipe_query = this.$route.query.recipe ? { recipe: this.$route.query.recipe } : {};
        this.$router.replace({
          name: "ThicknessAdjustmentSteelcase",
          params: { lineId: this.lineId },
          query: { hopper: hopper.id, ...selected_recipe_query },
        });
      }
    },
    async updateSelectedRecipe(recipe) {
      console.log("updateSelectedRecipe()", recipe);
      this.searchQueryRecipe = "";
      this.selected_recipe = recipe;
      this.target_throughput = recipe.target_throughput;
      this.powder_amount_settings = recipe.latest_powder_amount_settings.map(item =>
        item.amount ? parseFloat(parseFloat(item.amount).toFixed(1)) : null,
      );

      console.log("updateSelectedRecipe() target_throughput", this.target_throughput);

      this.showLineLayoutVisualization = true;

      this.$router.replace({
        name: "ThicknessAdjustmentSteelcase",
        params: { lineId: this.lineId },
        query: { hopper: this.selected_hopper.id, recipe: recipe.id },
      });
    },
    async getRecipes() {
      console.log("getRecipes()");
      try {
        let response = await axios.get("api/v1/steelcase/recipe/hopper/" + this.selected_hopper.id + "/");
        this.recipes = response.data.filter(recipe => recipe.active);

        if (this.recipes.length === 1) {
          await this.updateSelectedRecipe(this.recipes[0]);
          await this.clearThicknessAdjustmentsHistory(false);
        }

        // If a recipe was previously selected, re-select it from the new list
        if (this.selected_recipe != null) {
          let selected_recipe_in_list = this.recipes.find(
            recipe => recipe.id === this.selected_recipe.id,
          );
          await this.updateSelectedRecipe(selected_recipe_in_list);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getHoppers() {
      if (this.coating_booth.id === null) {
        return;
      }
      try {
        let response = await axios.get("api/v1/steelcase/hopper/coating-booth/" + this.coating_booth.id + "/");
        this.hoppers = response.data;

        if (this.hoppers.length === 1) {
          this.selected_hopper = this.hoppers[0];
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getMinMaxThroughput() {
      try {
        const result = await axios.get(
          `api/v1/steelcase/computeminmaxthroughput/coating_booth/${this.coating_booth.id}/`,
        );
        this.minimum_throughput = Math.floor(result.data.min_throughput);
      } catch (error) {
        console.error("Error fetching min/max throughput:", error);
        this.minimum_throughput = 0;
      }
    },
    clearCoatingMeasures() {
      this.measured_thickness_mean = null;
      this.measured_thickness_std = null;
    },
    onClickPrevPage() {
      if (this.currentPage > 0) {
        this.updateCurrentPage(this.currentPage - 1);
      }
    },
    onClickNextPage() {
      if (this.currentPage < this.thickness_adjustment_history.length - 1) {
        this.updateCurrentPage(this.currentPage + 1);
      }
    },
    async getThicknessAdjustmentHistory() {
      console.log("getThicknessAdjustmentHistory();");

      try {
        const response = await axios.get(`/api/v1/steelcase/thicknessadjustmenthistory/${this.selected_recipe.id}/`);
        const thickness_adjustment_history_unordered = response.data;

        this.thickness_adjustment_history = thickness_adjustment_history_unordered.sort((a, b) => a.id - b.id);

        if (this.thickness_adjustment_history.length > 0) {
          this.latest_thickness_adjustment =
            this.thickness_adjustment_history[this.thickness_adjustment_history.length - 1];

          this.measured_thickness_mean = this.latest_thickness_adjustment.measured_thickness_mean;
          this.measured_thickness_std = this.latest_thickness_adjustment.measured_thickness_std;
          this.minimum_target_thickness = this.latest_thickness_adjustment.minimum_target_thickness;
          this.target_throughput = this.latest_thickness_adjustment.target_throughput;
          await this.updateCurrentPage(this.thickness_adjustment_history.length - 1);

          if (this.firstAdjustmentDone || this.coatingMeasurementReady) {
            await this.drawThicknessGraph();
          }
        }
        console.log("thickness_adjustment_history: ", this.thickness_adjustment_history);
        console.log("latest_thickness_adjustment: ", this.latest_thickness_adjustment);
        console.log("measured_thickness_mean: ", this.measured_thickness_mean);
        console.log("measured_thickness_std: ", this.measured_thickness_std);
        console.log("target_throughput: ", this.target_throughput);
      } catch (error) {
        console.error(error);
      }
    },
    checkPowderAmountSettings() {
      console.log("checkPowderAmountSettings()");
      const negative_powder_amounts = this.powder_amount_settings.some(powder_amount_param => powder_amount_param <= 0);
      if (negative_powder_amounts && !this.invalid_powder_amount_shown) {
        this.$swal({
          title: "Invalid Powder Amount Setting results",
          text: `The Powder Throughput ${this.target_throughput.toFixed(1)} results in invalid powder amount parameters. Review your process.`,
          icon: "error",
          confirmButtonText: "OK",
        });
        this.invalid_powder_amount_shown = true;
      }
    },
    getPreviousTargetThroughput() {
      console.log("getPreviousTargetThroughput()");
      if (this.firstAdjustmentDone) {
        return this.thickness_adjustment_history[this.currentPage - 1].target_throughput;
      } else {
        return;
      }
    },
    isValidTargetThroughput() {
      let is_valid_target_throughput =
        this.target_throughput !== null && this.target_throughput !== "" && this.target_throughput > 0;

      return is_valid_target_throughput;
    },
    async getAdjustedTargetThroughput(gaussian_mean, gaussian_std) {
      console.log("getAdjustedTargetThroughput()");

      const body = {
        gaussian_mean: gaussian_mean,
        gaussian_std: gaussian_std,
        current_powder_throughput: this.target_throughput,
        minimum_target_thickness: this.minimum_target_thickness,
        min_powder_throughput: this.minimum_throughput,
        percentile: this.percentile,
      };
      console.log("getAdjustedTargetThroughput(): body ", body);
      const response = await axios.post("/api/v1/blueprint/nextoptimizedpowderthroughput/", body);
      const next_powder_throughput = response.data.next_powder_throughput;
      return next_powder_throughput;
    },
    async createThicknessAdjustment() {
      console.log("createThicknessAdjustment();");

      const response = await axios.post(`/api/v1/steelcase/thicknessadjustmenthistory/${this.selected_recipe.id}/`, {
        coating_booth: this.coating_booth.id,
        hopper: this.selected_hopper.id,
        recipe: this.selected_recipe.id,
        minimum_target_thickness: this.minimum_target_thickness,
        target_throughput: this.target_throughput,
        expected_thickness_mean: this.expected_thickness_mean,
        expected_thickness_std: this.expected_thickness_std,
        powder_amount_settings: this.powder_amount_settings,
      });

      if (response.status == 201) {
        this.latest_thickness_adjustment = JSON.parse(JSON.stringify(response.data));
      }
    },
    async patchLatestThicknessAdjustment(
      minimum_target_thickness,
      target_throughput,
      powder_amount_settings,
      measured_thickness_mean,
      measured_thickness_std,
      expected_thickness_mean,
      expected_thickness_std,
    ) {
      console.log("patchLatestThicknessAdjustment()");
      try {
        if (target_throughput == null || target_throughput == "" || this.latest_thickness_adjustment.id == null) {
          return;
        }

        const response = await axios.patch(
          `/api/v1/steelcase/thicknessadjustmenthistorydetail/${this.latest_thickness_adjustment.id}/`,
          {
            minimum_target_thickness: minimum_target_thickness,
            target_throughput: target_throughput,
            mean_thickness: measured_thickness_mean,
            powder_amount_settings: powder_amount_settings,
            measured_thickness_mean: measured_thickness_mean,
            measured_thickness_std: measured_thickness_std,
            expected_thickness_mean: expected_thickness_mean,
            expected_thickness_std: expected_thickness_std,
          },
        );
        this.latest_thickness_adjustment = JSON.parse(JSON.stringify(response.data));

        this.thickness_adjustment_history[this.thickness_adjustment_history.length - 1] =
          this.latest_thickness_adjustment;
      } catch (error) {
        console.error(error);
      }

      // Log on console the patch info
      console.log("target_throughput", target_throughput);
      console.log("measured_thickness_mean", measured_thickness_mean);
      console.log("powder_amount_settings", powder_amount_settings);
    },
    async computeNextThicknessAdjustment() {
      this.$store.state.isLoading = true;

      console.log("computeNextThicknessAdjustment()");

      // 1. Compute adjusted target_throughput
      const next_target_throughput = await this.getAdjustedTargetThroughput(
        this.measured_thickness_mean,
        this.measured_thickness_std,
      );

      // 2. Compute next expected mean thickness
      const next_expected_thickness_mean = await this.computeNextExpectedMeanThicknessFromThroughput(
        next_target_throughput,
        this.target_throughput,
        this.measured_thickness_mean,
      );

      // 3. Update state variables with new values

      // target_throughput is updated with the new value
      this.target_throughput = parseFloat(next_target_throughput.toFixed(1));

      // expected_thickness_mean is updated with the new predicted expected mean thickness
      this.expected_thickness_mean = parseFloat(next_expected_thickness_mean.toFixed(1));

      // expected_thickness_std is updated with the std of the previous measurements
      this.expected_thickness_std = this.measured_thickness_std;

      // 4. Compute new powder amount settings given new target throughput
      await this.computeNewPowderAmountSettings();

      // Clear previous coating measures, measured_thickness_mean and measured_thickness_std,
      // as the user will have to input new coating measures
      this.clearCoatingMeasures();

      // 5. Create new Thickness Adjustment step with following new values:
      // - target_throughput
      // - expected_thickness_mean
      // - expected_thickness_std
      // - powder_amount_settings
      await this.createThicknessAdjustment();

      // 6. Get thickness adjustment history to update local history variables
      await this.getThicknessAdjustmentHistory();

      // 7. Re-Plot the thickness distirbution graph with the last page distributions
      // (will show only previous and expected distributions)
      this.drawThicknessGraph();
      this.$store.state.isLoading = false;
    },
    async computeNextExpectedMeanThicknessFromThroughput(new_throughput, old_throughput, measured_thickness_mean) {
      console.log("computeNextExpectedMeanThicknessFromThroughput()");
      const response = await axios.post("/api/v1/blueprint/throughputtothickness/", {
        old_throughput: old_throughput,
        new_throughput: parseFloat(new_throughput),
        old_mean_thickness: measured_thickness_mean,
        std_thickness: this.measured_thickness_std,
        min_thickness: this.minimum_target_thickness,
      });

      if (!response.data.is_valid) {
        await this.$swal({
          title: "Target Throughput out of range",
          text:
            "Throughput adjustment may result in a thickness distribution where more than 10% of the " +
            "coating is below the desired minimum thickness. To ensure compliance, the throughput" +
            `has been adjusted to the minimum acceptable level of ${response.data.new_throughput.toFixed(1)} `,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
      return response.data.new_mean_thickness;
    },
    async clearThicknessAdjustmentsHistory(dialog = true) {
      console.log("clearThicknessAdjustmentsHistory()");
      try {
        let result;
        if (dialog) {
          result = await this.$swal({
            title: "Are you sure?",
            text: "This will clear all adjustment steps.",
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: "Cancel",
            confirmButtonText: "Yes, clear history",
            customClass: {
              confirmButton: "btn btn-warning w-40 px-1 m-2",
              cancelButton: "btn bg-gradient-danger w-40 px-1 m-2",
            },
            reverseButtons: true,
            buttonsStyling: false,
          });
        } else {
          result = { isConfirmed: true };
        }

        if (result.isConfirmed) {
          // Marks Thickness Adjustment Hisory objects as cleared adjustments
          await axios.delete(`/api/v1/steelcase/thicknessadjustmenthistory/${this.selected_recipe.id}/`);

          if (dialog) {
            await this.$swal({
              title: `Thickness Adjustment History of Recipe ${this.selected_recipe.category} cleared`,
              text: "The thickness adjustment history has been successfully cleared. Page will be reloaded.",
              icon: "success",
              confirmButtonText: "OK",
            });
          }

          this.thickness_adjustment_history = [];
          this.target_throughput = this.selected_recipe.target_throughput;
          this.clearCoatingMeasures();
          this.show_thickness_chart_title = "false";
          await this.createThicknessAdjustment();
          await this.getThicknessAdjustmentHistory();
          await this.drawThicknessGraph();
        }
      } catch (error) {
        console.error(error);
      }
    },
    async updateCurrentPage(index) {
      // if (this.isInLastPage) {
      //   this.last_page_edited_measured_thickness_mean = ;
      //   this.last_page_edited_measured_thickness_std = ;
      // }
      this.currentPage = index;

      this.target_throughput = this.thickness_adjustment_history[index].target_throughput;
      this.measured_thickness_mean = this.thickness_adjustment_history[index].measured_thickness_mean;
      this.measured_thickness_std = this.thickness_adjustment_history[index].measured_thickness_std;
      this.expected_thickness_mean = this.thickness_adjustment_history[index].expected_thickness_mean;
      this.expected_thickness_std = this.thickness_adjustment_history[index].expected_thickness_std;

      this.powder_amount_settings = this.thickness_adjustment_history[index].powder_amount_settings;

      this.drawThicknessGraph();
    },
    drawBarChart() {
      console.log("drawBarChart()");
      eventBus.emit("draw-bar-chart");
    },
    drawThicknessGraph() {
      console.log("drawThicknessGraph()");
      this.$store.state.gaussianThicknessAdjustmentChart.datasets = [];
      this.$store.state.gaussianThicknessAdjustmentChart.labels = [];
      this.generateXLabels();
      this.prevAdjustmentsThicknessMeasurementsUpdateChartData();
      this.thicknessMeasurementsUpdateChartData();
      this.expectedThicknessUpdateChartData();

      if (this.firstAdjustmentDone || this.currentPageCoatingMeasuresFilled) {
        this.show_thickness_chart_title = "true";
      }
      eventBus.emit("draw-gaussian-chart");
    },
    generateXLabels() {
      if (this.firstAdjustmentDone && !this.isInFirstPage && this.currentPageCoatingMeasuresFilled) {
        // Show all 3 distributions in the chart
        const mu = this.thickness_adjustment_history[this.currentPage].measured_thickness_mean;
        const sigma = this.thickness_adjustment_history[this.currentPage].measured_thickness_std;

        const expected_mu = this.thickness_adjustment_history[this.currentPage].expected_thickness_mean;
        const expected_sigma = this.thickness_adjustment_history[this.currentPage].expected_thickness_std;

        const previous_measures_mu = this.thickness_adjustment_history[this.currentPage - 1].measured_thickness_mean;
        const previous_measures_sigma = this.thickness_adjustment_history[this.currentPage - 1].measured_thickness_std;

        this.$store.state.gaussianThicknessAdjustmentChart.labels = this.generateXforMultipleMuSigma([
          [mu, sigma],
          [previous_measures_mu, previous_measures_sigma],
          [expected_mu, expected_sigma],
        ]);
      } else if (this.firstAdjustmentDone && !this.isInFirstPage) {
        // Show only 2 distributions in the chart, the previous and the expected
        const previous_thickness_adjustment = this.thickness_adjustment_history[this.currentPage - 1];

        const previous_measures_mu = previous_thickness_adjustment.measured_thickness_mean;
        const previous_measures_sigma = previous_thickness_adjustment.measured_thickness_std;

        const expected_mu = this.expected_thickness_mean;
        const expected_sigma = this.expected_thickness_std;

        this.$store.state.gaussianThicknessAdjustmentChart.labels = this.generateXforMultipleMuSigma([
          [previous_measures_mu, previous_measures_sigma],
          [expected_mu, expected_sigma],
        ]);
      } else if (this.currentPageCoatingMeasuresFilled) {
        // Show only 1 distribution chart
        const mu = this.thickness_adjustment_history[this.currentPage].measured_thickness_mean;
        const sigma = this.thickness_adjustment_history[this.currentPage].measured_thickness_std;
        this.$store.state.gaussianThicknessAdjustmentChart.labels = this.generateXforMultipleMuSigma([[mu, sigma]]);
      }

      const Xarray = this.$store.state.gaussianThicknessAdjustmentChart.labels.map(num => parseFloat(num));

      this.$store.state.minTargetThicknessXAxisIndex = Xarray.findIndex(num => num > this.minimum_target_thickness);
      this.$store.state.maxTargetThicknessXAxisIndex = null;
    },
    prevAdjustmentsThicknessMeasurementsUpdateChartData() {
      if (!this.firstAdjustmentDone || this.isInFirstPage) {
        return;
      }
      const previous_thickness_adjustment = this.thickness_adjustment_history[this.currentPage - 1];
      const previous_measures_mu = previous_thickness_adjustment.measured_thickness_mean;
      const previous_measures_sigma = previous_thickness_adjustment.measured_thickness_std;

      this.$store.state.gaussianThicknessAdjustmentChart.datasets[1] = {
        label: "Last Thickness Distribution",
        data: this.generateYwithXvalues(
          previous_measures_sigma,
          previous_measures_mu,
          this.$store.state.gaussianThicknessAdjustmentChart.labels,
        ),
      };
    },
    thicknessMeasurementsUpdateChartData() {
      if (!this.currentPageCoatingMeasuresFilled) {
        return;
      }
      const mu = this.thickness_adjustment_history[this.currentPage].measured_thickness_mean;
      const sigma = this.thickness_adjustment_history[this.currentPage].measured_thickness_std;

      this.$store.state.gaussianThicknessAdjustmentChart.datasets[4] = {
        label: "Current Thickness Distribution",
        data: this.generateYwithXvalues(sigma, mu, this.$store.state.gaussianThicknessAdjustmentChart.labels),
      };
    },
    expectedThicknessUpdateChartData() {
      if (!this.firstAdjustmentDone || this.currentPage == 0) {
        return;
      }

      const expected_mu = this.thickness_adjustment_history[this.currentPage].expected_thickness_mean;
      const expected_sigma = this.thickness_adjustment_history[this.currentPage].expected_thickness_std;

      this.$store.state.gaussianThicknessAdjustmentChart.datasets[3] = {
        label: "Expected Thickness",
        data: this.generateYwithXvalues(
          expected_sigma,
          expected_mu,
          this.$store.state.gaussianThicknessAdjustmentChart.labels,
        ),
      };
    },
    async computeNewPowderAmountSettings() {
      console.log("computeNewPowderAmountSettings()");
      try {
        const response = await axios.post("api/v1/steelcase/computerecipepowderamountsettingsfromtargetthroughput/", {
          recipe_id: this.selected_recipe.id,
          target_throughput: this.target_throughput,
          target_throughput_bottom: this.selected_recipe.target_throughput_bottom,
        });
        const powder_amount_settings_by_gun_index = response.data.powder_amount_settings_by_gun_index;
        this.powder_amount_settings = Array.from({ length: this.total_line_guns }, (_, gun_index) =>
          powder_amount_settings_by_gun_index[gun_index + 1].new_powder_amount_setting
            ? parseFloat(
                parseFloat(powder_amount_settings_by_gun_index[gun_index + 1].new_powder_amount_setting).toFixed(1),
              )
            : null,
        );
      } catch (error) {
        console.error(error);
      }
      return null;
    },
    async updateRecipeSettings() {
      console.log("updateRecipeSettings()");
      this.$store.state.isLoading = true;

      if (this.selected_hopper == null || this.selected_recipe == null) {
        return;
      }
      try {
        const response = await axios.get("api/v1/steelcase/guns/coating-booth/" + this.coating_booth.id + "/");
        console.log("response.data", response.data);

        const powder_amount_settings_per_gun = Array.from({ length: this.total_line_guns }, (_, gun_index) => ({
          gun_id: response.data.find(gun => gun.gun_index == gun_index + 1).id,
          powder_amount_setting: this.powder_amount_settings[gun_index],
        }));

        await axios.post("api/v1/steelcase/createpowderamountsettingsbyrecipe/", {
          recipe_id: this.selected_recipe.id,
          powder_amount_settings_per_gun: powder_amount_settings_per_gun,
        });

        await axios.patch("api/v1/steelcase/recipe/" + this.selected_recipe.id + "/", {
          target_throughput: this.target_throughput,
        });

        this.$store.state.isLoading = false;

        await this.$swal({
          title: "Recipe succesfully updated",
          text: `Recipe updated with new Powder Throughput ${this.target_throughput} and new Powder Amount settings.`,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "btn btn-success w-40 px-1 m-2",
          },
          buttonsStyling: false,
        });

        await this.getRecipes();
        await this.clearThicknessAdjustmentsHistory(false);

      } catch (error) {
        console.error(error);
      }
    },
    closeDropdown() {
      this.hopperDropdownVisible = false;
      this.recipeDropdownVisible = false;
    },
  },
};
</script>

<style scoped>
.bottom-gun-table h6,
.bottom-gun-table .table-sm th,
.bottom-gun-table .table-sm td {
  color: #adadad;
}
.table-sm th,
.table-sm td {
  width: 50%;
}
</style>
